/**
 * EmailMessageComposition Component
 *
 * Phase 4: Email content step for trigger creation
 * Integrates GrapesJS HTML editor, template management, and variable insertion
 */
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FolderOpen, Save, Info, AlertCircle, Loader2 } from 'lucide-react';
import GrapesJSEditor from '@/components/email/editor/GrapesJSEditor';
import { TemplateLibraryModal, TemplateSaveDialog } from '@/components/email/templates';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth';
import { supabase } from '@/integrations/supabase/client';

interface EmailMessageCompositionProps {
  triggerData: {
    email_subject: string;
    email_content_html: string;
    email_content_text: string;
    email_design_json?: any;
    trigger_id?: string; // For GrapesJS persistence
  };
  updateTriggerData: (data: Partial<EmailMessageCompositionProps['triggerData']>) => void;
  errors?: Record<string, string>;
  onEditorReady?: (saveFunction: () => Promise<void>, editorInstance: any) => void; // ✅ NEW - callback to pass refs to parent
}

const EmailMessageComposition: React.FC<EmailMessageCompositionProps> = ({
  triggerData,
  updateTriggerData,
  errors = {},
  onEditorReady // ✅ NEW - receive callback from parent
}) => {
  const { user } = useAuth();
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showTemplateSaveDialog, setShowTemplateSaveDialog] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);

  // Store reference to GrapesJS manual save function and editor instance
  const editorSaveRef = useRef<(() => Promise<void>) | null>(null);
  const editorInstanceRef = useRef<any | null>(null);

  const handleLoadTemplate = async (designJson: any, templateId?: string) => {
    if (editorInstanceRef.current) {
      try {
        console.log('[EmailMessageComposition] Loading template:', templateId);

        // Load the design into the editor
        editorInstanceRef.current.loadProjectData(designJson);

        // IMPORTANT: After loading template, restore user's media library assets
        // This prevents the template's assets from overriding the user's actual images
        if (user?.organisationId) {
          try {
            const { data: mediaAssets, error } = await supabase
              .from('restaurant_media')
              .select('id, public_url, original_filename, media_type')
              .eq('organisation_id', user.organisationId)
              .eq('media_type', 'image')
              .eq('status', 'completed')
              .order('created_at', { ascending: false });

            if (!error && mediaAssets && mediaAssets.length > 0) {
              const assetManager = editorInstanceRef.current.AssetManager;

              // Clear existing assets and add media library images
              assetManager.clear();

              // IMPORTANT: Reverse array before adding because AssetManager.add() prepends
              // Query returns newest-first, reverse makes oldest-first for prepending
              // Final result: newest at top
              mediaAssets.reverse().forEach((asset) => {
                assetManager.add({
                  type: 'image',
                  src: asset.public_url,
                  name: asset.original_filename || 'Image',
                  id: asset.id
                });
              });

              console.log(`[EmailMessageComposition] Loaded ${mediaAssets.length} assets from media library`);
            }
          } catch (assetError) {
            console.error('[EmailMessageComposition] Error loading media library assets:', assetError);
            // Non-fatal error - template still loads, just without pre-populated assets
          }
        }

        // FIX: Wait a moment for GrapesJS to process the loaded data
        // Then get the HTML output to update trigger data
        setTimeout(() => {
          if (editorInstanceRef.current) {
            try {
              const html = editorInstanceRef.current.getHtml();
              const plainText = html.replace(/<[^>]*>/g, ''); // Strip HTML for plain text

              // Update form data with complete template data
              updateTriggerData({
                email_design_json: designJson,
                email_content_html: html,
                email_content_text: plainText,
                email_template_id: templateId || null  // ✅ Save template ID for tracking
              });

              console.log('[EmailMessageComposition] Template data updated with HTML');
            } catch (error) {
              console.error('[EmailMessageComposition] Error getting HTML after load:', error);
            }
          }
        }, 500); // Wait 500ms for GrapesJS to render

        setShowTemplateLibrary(false);

        toast({
          title: 'Template Loaded',
          description: 'Template has been loaded into the editor. You can now customize it.',
        });
      } catch (error) {
        console.error('[EmailMessageComposition] Error loading template:', error);
        toast({
          title: 'Error',
          description: 'Failed to load template into editor',
          variant: 'destructive'
        });
      }
    } else {
      console.error('[EmailMessageComposition] Editor instance not available');
      toast({
        title: 'Error',
        description: 'Editor not ready. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleSaveAsTemplate = async () => {
    console.log('[EmailMessageComposition] Save as template clicked');

    // FIX: Manually get current editor state to ensure we have latest data
    // This prevents saving stale data and ensures all attributes are preserved
    let currentProjectData = triggerData.email_design_json;
    let currentHtml = triggerData.email_content_html;

    if (editorInstanceRef.current) {
      console.log('[EmailMessageComposition] Getting current editor state directly');
      try {
        currentProjectData = editorInstanceRef.current.getProjectData();
        currentHtml = editorInstanceRef.current.getHtml();

        // Update trigger data with current state
        updateTriggerData({
          email_design_json: currentProjectData,
          email_content_html: currentHtml,
          email_content_text: currentHtml.replace(/<[^>]*>/g, '')
        });
      } catch (error) {
        console.error('[EmailMessageComposition] Error getting editor state:', error);
      }
    } else {
      console.warn('[EmailMessageComposition] Editor instance not available, using existing state');
    }

    setShowTemplateSaveDialog(true);
  };

  const handleTemplateSaved = (templateId: string) => {
    console.log('[EmailMessageComposition] Template saved:', templateId);
    toast({
      title: 'Template Saved',
      description: 'Your email design has been saved as a template for future use.',
    });
  };

  const handleManualSave = async () => {
    if (!editorSaveRef.current) {
      return;
    }

    try {
      setManualSaving(true);
      console.log('[EmailMessageComposition] Manual save triggered');

      await editorSaveRef.current();

      console.log('[EmailMessageComposition] Manual save completed successfully');
      toast({
        title: 'Saved',
        description: 'Your email content has been saved',
      });
    } catch (error) {
      console.error('[EmailMessageComposition] Manual save error:', error);
      toast({
        title: 'Error',
        description: 'Failed to save email content',
        variant: 'destructive'
      });
    } finally {
      setManualSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium mb-2">Email Content</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Create the email that will be sent when the trigger activates.
          Use dynamic variables to personalize with customer information.
        </p>
      </div>

      {/* Show validation errors if present */}
      {errors.email_subject && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errors.email_subject}</AlertDescription>
        </Alert>
      )}
      {errors.email_content_html && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errors.email_content_html}</AlertDescription>
        </Alert>
      )}

      {/* Subject Line */}
      <div className="space-y-2">
        <Label htmlFor="email_subject">Subject Line *</Label>
        <Input
          id="email_subject"
          value={triggerData.email_subject}
          onChange={(e) => updateTriggerData({ email_subject: e.target.value })}
          placeholder="e.g., Welcome back, {{firstName}}! Here's 20% off"
          className="font-medium"
        />
        <p className="text-xs text-muted-foreground">
          Use {'{'}{'{'}firstName{'}'}{'}'},  {'{'}{'{'}lastName{'}'}{'}'},  {'{'}{'{'}totalOrders{'}'}{'}'} for personalization
        </p>
      </div>

      {/* Template Management Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={() => setShowTemplateLibrary(true)}
          variant="outline"
          size="sm"
          type="button"
        >
          <FolderOpen className="mr-2 h-4 w-4" />
          Load Template
        </Button>
        <Button
          onClick={handleSaveAsTemplate}
          variant="outline"
          size="sm"
          type="button"
        >
          <Save className="mr-2 h-4 w-4" />
          Save as Template
        </Button>
        <Button
          onClick={handleManualSave}
          disabled={manualSaving}
          variant="outline"
          size="sm"
          type="button"
        >
          {manualSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save
            </>
          )}
        </Button>
      </div>

      {/* HTML Editor */}
      <div className="border rounded-lg overflow-hidden">
        <GrapesJSEditor
          initialHtml={triggerData.email_content_html}
          initialProject={triggerData.email_design_json}
          projectId={triggerData.trigger_id}
          projectType="email_trigger"
          height="700px"
          onChange={(html, css, project) => {
            console.log('[EmailMessageComposition] GrapesJS onChange triggered, HTML length:', html.length);
            updateTriggerData({
              email_content_html: html,
              email_content_text: html.replace(/<[^>]*>/g, ''), // Strip HTML for plain text
              email_design_json: project
            });
          }}
          onEditorReady={(saveFunction, editorInstance) => {
            console.log('[EmailMessageComposition] GrapesJS editor ready, save function and editor instance captured');
            editorSaveRef.current = saveFunction;
            editorInstanceRef.current = editorInstance;
            // ✅ Pass refs up to parent form component
            if (onEditorReady) {
              onEditorReady(saveFunction, editorInstance);
            }
          }}
        />
      </div>

      {/* Help text for dynamic variables */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Personalization Variables:</strong> Use the variable insertion buttons in the editor toolbar
          to add customer data ({'{'}{'{'}firstName{'}'}{'}'},  {'{'}{'{'}totalOrders{'}'}{'}'}),
          restaurant URLs ({'{'}{'{'}preferredRestaurantOrderingUrl{'}'}{'}'}),
          and system links ({'{'}{'{'}unsubscribeUrl{'}'}{'}'}).
        </AlertDescription>
      </Alert>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Dynamic Restaurant URLs:</strong> When you use  {'{'}{'{'}preferredRestaurantOrderingUrl{'}'}{'}'} or similar variables, the URLs will automatically resolve to the customer's most recently ordered restaurant.
          If URLs are missing, the system will use organisation defaults or the trigger's restaurant as fallbacks.
        </AlertDescription>
      </Alert>

      {/* Template Modals */}
      <TemplateLibraryModal
        open={showTemplateLibrary}
        onOpenChange={setShowTemplateLibrary}
        onTemplateSelect={handleLoadTemplate}
        organisationId={user?.organisationId || null}
      />

      <TemplateSaveDialog
        open={showTemplateSaveDialog}
        onClose={() => setShowTemplateSaveDialog(false)}
        designJson={triggerData.email_design_json || {}}
        templateContentHtml={triggerData.email_content_html || ''}
        onSaveSuccess={handleTemplateSaved}
        organisationId={user?.organisationId || ''}
        restaurantId={user?.restaurantId}
      />
    </div>
  );
};

export default EmailMessageComposition;
